// features/cx/disconnect/api.ts

import {
  MeterReadingsFilters,
  TimeSlotChoice,
  UtilityConfigFilters,
  UtilityRequestConfiguration,
} from "@features/cx/shared/types/consumerRequest";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  communicationApiClient,
  cxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { ApiResponse, DropdownItem } from "@shared/api/types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ActivityLogFilters,
  ActivityLogsResponse,
  ConsumerBillData,
  ConsumerBillFilters,
  ConsumerMetaData,
  ConsumerSearchFilters,
  ConsumerSearchResult,
  ConsumerSupportDisconnectPayload,
  CreateBillGenerationPayload,
  CreateDisconnectionRequestPayload,
  DisconnectionFilters,
  DisconnectionRequest,
  DisconnectionSummary,
  SingleBillApprovePayload,
  UpdateBillGenerationPayload,
  UpdateDisconnectionRequestPayload,
} from "./types";
import { normalizeDate } from "@shared/utils/dateUtils";

// API Functions
export const disconnectionApi = {
  // Consumer Search
  searchConsumer: async (
    filters: ConsumerSearchFilters
  ): Promise<{ result: ConsumerSearchResult[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-search",
      ["info"],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("search_data", filters.searchData);
        query.push("request_type", filters.requestType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerSearchResult[] }>(
      response.data
    );
  },

  // Consumer Meta Data
  getConsumerMetaData: async (params: {
    consumerId: number;
    remoteUtilityId: number;
  }): Promise<{ result: ConsumerMetaData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-meta-data",
      (query) => {
        query.push("consumer_id", params.consumerId.toString());
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerMetaData }>(response.data);
  },

  getUtilityRequestConfiguration: async (
    filters: UtilityConfigFilters
  ): Promise<{ result: UtilityRequestConfiguration[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("request_type", filters.requestType);
        if (filters.disablePagination) query.push("disable_pagination", "true");
        if (filters.searchData) query.push("search_data", filters.searchData);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: UtilityRequestConfiguration[] }>(
      response.data
    );
  },

  // Consumer Bill Data
  getConsumerBill: async (
    filters: ConsumerBillFilters
  ): Promise<{ result: ConsumerBillData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-bill",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("consumer_id", filters.consumerId.toString());
        query.push("fetch_last_six_records", "True");
        query.push("bill_data", "True");
        return query;
      }
    );

    const response = await cxApiClient.get<{ result: ConsumerBillData }>(url);
    return transformJsonToObject<{ result: ConsumerBillData }>(response.data);
  },

  // Disconnection Request List
  getDisconnectionRequests: async (
    filters: DisconnectionFilters
  ): Promise<ApiResponse<DisconnectionRequest>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", getRemoteUtilityId());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.name) query.push("name", filters.name);
        return query;
      }
    );

    const response = await cxApiClient.get<ApiResponse<DisconnectionRequest>>(
      url
    );
    return transformJsonToObject<ApiResponse<DisconnectionRequest>>(
      response.data
    );
  },

  // Add this method to your disconnection API object

  exportDisconnectionRequests: async (
    filters: DisconnectionFilters
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", getRemoteUtilityId());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.name) query.push("name", filters.name);
        query.push("export_format", "csv");
        return query;
      }
    );

    const response = await cxApiClient.get(url);

    return response.data;
  },

  // Create Disconnection Request
  createDisconnectionRequest: async (
    payload: CreateDisconnectionRequestPayload
  ): Promise<DisconnectionRequest> => {
    const url = ApiEndpoints.createUrl("cx", "connection-status-request");
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<DisconnectionRequest>(response.data);
  },

  // Get Disconnection Request Detail
  getDisconnectionRequestDetail: async (
    requestId: number,
    remoteUtilityId: string
  ): Promise<DisconnectionRequest> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      `connection-request-detail`,
      [requestId.toString()],
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<DisconnectionRequest>(response.data);
  },

  // Update Disconnection Request
  updateDisconnectionRequest: async (
    requestId: number,
    payload: UpdateDisconnectionRequestPayload
  ): Promise<DisconnectionRequest> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()]
    );
    const response = await cxApiClient.put(url, payload);
    return transformJsonToObject<DisconnectionRequest>(response.data);
  },

  // Delete Disconnection Request
  deleteDisconnectionRequest: async (requestId: number): Promise<void> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()]
    );
    await cxApiClient.delete(url);
  },

  // Get Disconnection Summary
  getDisconnectionSummary: async (params: {
    remoteUtilityId: number;
    requestType: string;
  }): Promise<DisconnectionSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request-summary",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("request_type", params.requestType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<DisconnectionSummary>(response.data.result);
  },

  // Bill Generation APIs
  createBillGeneration: async (
    payload: CreateBillGenerationPayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("bx", "single-bill-generation");
    const response = await bxApiClient.post(url, payload);
    return response.data;
  },

  updateBillGeneration: async (
    billId: number,
    payload: UpdateBillGenerationPayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "bx",
      "single-bill-generation",
      [billId.toString()]
    );
    const response = await bxApiClient.patch(url, payload);
    return response.data;
  },

  approveSingleBill: async (
    billId: number,
    payload: SingleBillApprovePayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "bx",
      "single-bill-approve",
      [billId.toString()]
    );
    const response = await bxApiClient.put(url, payload);
    return response.data;
  },

  // Consumer Support Disconnect
  disconnectConsumerSupport: async (
    consumerId: number,
    payload: ConsumerSupportDisconnectPayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-support",
      ["disconnect", consumerId.toString()]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  getStatus: async (params: {
    remoteUtilityId: number;
    tab: number;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["consumer_support_request_status"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("tab", params.tab);
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getCategory: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("request_type", "Disconnect Permanent");
        query.push("disable_pagination", true);
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return response?.data.result.map((item) => ({
      label: item.name,
      value: item.name,
    }));
  },

  // Activity Logs
  getActivityLogs: async (
    filters: ActivityLogFilters
  ): Promise<ActivityLogsResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "activity-log",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("request_id", filters.requestId.toString());
        query.push("module", filters.module);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      }
    );

    const response = await communicationApiClient.get(url);
    return transformJsonToObject<ActivityLogsResponse>(response.data);
  },

  getTimeSlotChoices: async (): Promise<{ result: TimeSlotChoice[] }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/time_slot");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: TimeSlotChoice[] }>(response.data);
  },

  getBillTemplates: async (remoteUtilityId: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "get-bill-template",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        return query;
      }
    );
    const response = await cxApiClient.get(url);

    return response.data.result;
  },

  getMeterReadings: async (filters: MeterReadingsFilters): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "meter-reading-details",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        // Add each meter_id separately
        filters.meterId.forEach((id) => {
          query.push("meter_id", id.toString());
        });
        query.push("invoice_start_date", normalizeDate(filters.startDate));
        query.push("invoice_end_date", normalizeDate(filters.endDate));

        // query.push("invoice_start_date", filters.startDate);
        // query.push("invoice_end_date", filters.endDate);
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return response.data.result;
  },
};

export const submitConsumerSelection = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `single-bill-generation`
  );
  const response = await bxApiClient.post(url, payload);
  return response.data?.result;
};

export const downloadBillTemplate = async (billId: string): Promise<Blob> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `render-dynamic-template/${billId}`
  );
  const response = await bxApiClient.get(url, {
    responseType: "blob",
  });
  return response.data;
};

export const approveBill = async (billId: any, billDraftId: number) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `single-bill-approve/${billId}`,
    (query) => {
      query.push("bill_draft_id", billDraftId);
    }
  );
  const response = await bxApiClient.put(url, {
    send_communication: true,
  });
  return response.data?.result;
};


export const approveDiscoonnectionBill = async (billId: any, requestID: number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    `single-bill-approve/${billId}/${requestID}`,  
    (query) => {
      query.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );
  
  const response = await cxApiClient.put(url);
  return response.data?.result;
};

export const rejectBill = async (billId: any) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `bill/${billId}/REJECTED`
  );
  const response = await bxApiClient.put(url);
  return response.data?.result;
};

export const updateDisconnection = async (
  requestId: number,
  payload: UpdateDisconnectionRequestPayload
): Promise<DisconnectionRequest> => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "cx",
    "disconnect-consumer-request",
    [requestId.toString()]
  );
  const response = await cxApiClient.put(url, payload);
  return transformJsonToObject<DisconnectionRequest>(response.data);
};

export const getWorkOrderDetails = async (search: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "search-job-card",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("search_data", search);

      return query;
    }
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const getMeterInfo = async (
  deviceNo: string,
  utilityService: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "search-meter",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("device_number", deviceNo);
      query.push("utility_service", utilityService);
      query.push("page", 1);
      query.push("limit", 5);

      return query;
    }
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};

export const getMeterHistory = async (
  consumerId: number,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "meter-history",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("consumer_id", consumerId);
      return query;
    }
  );
  const response = await cxApiClient.get(url);
  return response.data.result;
};